import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type {
  DocReadiness,
  ParticipantKycRole,
  ParticipantKycStatus,
  ShipmentResponse,
} from '../../api/types';
import { StatusPill } from '../../components/ui/StatusPill';
import { StatusIcon, type StatusIconName } from '../../components/ui/StatusIcon';
import { Notice } from '../../components/ui/Notice';
import { kycStatusColor } from '../../components/domain/statusColor';
import { kycGate, readinessMissing, type KycGateEffect } from './kycGates';
import styles from './ParticipantKycSection.module.css';

interface Props {
  /** `participantKyc` (who, and their KYC) plus `driverReadiness`, read at the shipment's status. */
  shipment: Pick<ShipmentResponse, 'status' | 'participantKyc' | 'driverReadiness'>;
  /** Return path for the profile links. */
  from: string;
}

const ROLE_LABEL: Record<ParticipantKycRole, string> = {
  SHIPPER: 'Shipper',
  DRIVER: 'Driver',
  PARTNER: 'Partner',
};

type Pill = { label: string; icon: StatusIconName; bg: string; text: string };

function statusDisplay(status: ParticipantKycStatus, role: ParticipantKycRole): Pill {
  switch (status) {
    case 'VERIFIED':
      return { label: 'Verified', icon: 'check', ...kycStatusColor('VERIFIED') };
    case 'SUBMITTED':
      return { label: 'Submitted', icon: 'hourglass', ...kycStatusColor('SUBMITTED') };
    case 'REJECTED':
      return { label: 'Rejected', icon: 'close', ...kycStatusColor('REJECTED') };
    case 'MISSING':
      return { label: `No ${ROLE_LABEL[role].toLowerCase()}`, icon: 'close', ...kycStatusColor('REJECTED') };
    case 'PENDING':
    default:
      return { label: 'Pending', icon: 'schedule', ...kycStatusColor('PENDING') };
  }
}

function docDisplay(item: string, status: DocReadiness): Pill {
  switch (status) {
    case 'VERIFIED':
      return { label: `${item} verified`, icon: 'check', ...kycStatusColor('VERIFIED') };
    case 'UPLOADED':
      return { label: `${item} uploaded`, icon: 'hourglass', ...kycStatusColor('SUBMITTED') };
    case 'REJECTED':
      return { label: `${item} rejected`, icon: 'close', ...kycStatusColor('REJECTED') };
    case 'MISSING':
      return { label: `${item} missing`, icon: 'close', ...kycStatusColor('REJECTED') };
    case 'PENDING':
    default:
      return { label: `${item} pending`, icon: 'schedule', ...kycStatusColor('PENDING') };
  }
}

function gateNote(effect: KycGateEffect, role: ParticipantKycRole) {
  const who = ROLE_LABEL[role].toLowerCase();
  switch (effect) {
    case 'BLOCKS_AWARD':
      return { tone: 'danger' as const, text: `Blocks award — verify the ${who}` };
    case 'BLOCKS_ASSIGN':
      return { tone: 'danger' as const, text: `Blocks driver assignment — verify the ${who}` };
    case 'ALERT':
      return { tone: 'warning' as const, text: 'Not verified — alert, not blocking' };
    default:
      return null;
  }
}

function PillOf({ pill }: { pill: Pill }) {
  return (
    <StatusPill bg={pill.bg} text={pill.text} label={pill.label} icon={<StatusIcon name={pill.icon} />} />
  );
}

/**
 * KYC per participant at the shipment's status, with what it blocks (R18, rhino-api#294):
 * award (shipper, winning bidder), driver assignment (partner) or nothing (ops alert).
 * The driver row also carries the driver's readiness (KYC / RC / photo): warn only (R8).
 */
export function ParticipantKycSection({ shipment, from }: Props) {
  const { t } = useTranslation();
  const rows = kycGate(shipment);
  if (rows.length === 0) return null;
  const readiness = shipment.driverReadiness;
  const missing = readinessMissing(readiness);
  return (
    <section
      className={styles.section}
      aria-labelledby="participant-kyc-title"
      data-testid="participant-kyc-section"
    >
      <h4 id="participant-kyc-title" className={styles.title}>
        {t('shipments.participantKycSection.title')}
      </h4>
      {rows.map(({ entry, effect }, index) => {
        const role = ROLE_LABEL[entry.role] ?? entry.role;
        const name = entry.name?.trim() || (entry.userId != null ? `${role} #${entry.userId}` : '—');
        const note = gateNote(effect, entry.role);
        const showReadiness = entry.role === 'DRIVER' && !!readiness;
        return (
          <div
            key={`${entry.role}-${index}`}
            className={styles.row}
            data-testid={`participant-kyc-${entry.role}`}
          >
            <span className={styles.role} data-testid="participant-kyc-role">
              {role}
            </span>
            <span className={styles.name} data-truncate>
              {entry.userId != null ? (
                <Link to={`/users/${entry.userId}`} state={{ from }} className={styles.link} title={name}>
                  {name}
                </Link>
              ) : (
                <span title={name}>{name}</span>
              )}
            </span>
            <PillOf pill={statusDisplay(entry.kycStatus, entry.role)} />
            {showReadiness && (
              <div className={styles.full} data-testid="driver-readiness">
                <PillOf pill={docDisplay('KYC', readiness.kyc)} />
                <PillOf pill={docDisplay('RC', readiness.vehicleRc)} />
                <PillOf pill={docDisplay('Photo', readiness.vehiclePhoto)} />
              </div>
            )}
            {note && (
              <div className={styles.full}>
                <Notice tone={note.tone} testId={`kyc-note-${entry.role}`}>
                  {note.text}
                </Notice>
              </div>
            )}
            {showReadiness && missing.length > 0 && (
              <div className={styles.full}>
                <Notice tone="warning" testId="kyc-note-DRIVER">
                  {t('shipments.participantKycSection.driverNotReady', { items: missing.join(' / ') })}
                </Notice>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
